import * as React from "react";
import Card from "../../../components/DataDisplay/CardComponent";
import i18n from "../../../i18n/i18n";
import IInvoiceVm from "../../../models/home/Payment/IInvoiceVm";
import TextField from "../../../components/DataDisplay/TextFieldComponent";
import api from "../../../services/api";
import ICompanyVm from "../../../models/shared/ICompanyVm";
import Modal from "../../../components/DataDisplay/ModalComponent";
import ICountryVm from "../../../models/shared/ICountryVm";
import { IToastrMessage } from "../../../containers/Shared/ToastrContainer";
import IssuingAuthorityContactInfoComponent from "./IssuingAuthorityContactInfoComponent";
import CitizenInfoComponent from "./CitizenInfoComponent";
import ApplicationInfoComponent from "./ApplicationInfoComponent";
import AcceptTermsAndConditionsComponent from "./AcceptTermsAndConditionsComponent";
import IErrorVm from "../../../models/home/Error/IErrorVm";
import { SelectedPayorType } from "../Payment";
import Select from "../../../components/DataDisplay/SelectComponent";
import CountryHelper from "../../../services/countryHelper";

interface IOnlinePaymentForCitizenComponentProps {
  invoice: IInvoiceVm;
  selectedPayor: SelectedPayorType;
  validationErrors: IErrorVm[];
  onUpdateCompanyRegistrationIdentifier: (value: string) => void;
  onReplaceCompany: (company: ICompanyVm) => void;
  onUpdateSelectedPayor: (payorType: SelectedPayorType) => void;
  onUpdateCompanyName: (value: string) => void;
  onUpdateCoName: (value: string) => void;
  onUpdateCombinedAddressLine: (value: string) => void;
  onUpdatePostalDistrict: (value: string) => void;
  onUpdatePostalCode: (value: string) => void;
  onUpdateAreTermsAndConditionsAccepted: (value: boolean) => void;
  onUpdateCountry: (country: ICountryVm) => void;
  onSetToastMessage: (message: IToastrMessage) => void;
}

interface IPaymentState {
  showDialog: boolean;
}

export default class OnlinePaymentForCitizenComponent extends React.Component<IOnlinePaymentForCitizenComponentProps, IPaymentState> {
  constructor(props: IOnlinePaymentForCitizenComponentProps) {
    super(props);
    this.state = { showDialog: false };
  }

  // If companyBilled exist on invoice, we set SelectedPayorType to CompanyPaying.
  // If not, we assume that citizen is paying
  public componentDidMount() {
    if (!!this.props.invoice.companyBilled) {
      this.props.onUpdateSelectedPayor(SelectedPayorType.CompanyPaying);
    } else {
      this.props.onUpdateSelectedPayor(SelectedPayorType.CitizenPaying);
    }
  }

  // Search for a company by its companyRegistrationIdentifier
  public async getCompanyFromCVR(companyRegistrationIdentifier: string, invoiceId: string) {
    // Fetch company
    const company = await api.company.getCompanyFromCvr(companyRegistrationIdentifier, invoiceId);

    // Find country by the countryCode
    let country: ICountryVm = CountryHelper.getCountryByCountryCode(company.address.countryCode, false);

    // If no country found, try by countryName
    if (!country) {
      country = CountryHelper.getCountryByCountryName(company.address.countryName, false);
    }

    // If country is still not found, set default to "Danmark", and show error message about updating manually from list
    if (!country) {
      country = CountryHelper.getCountryByCountryCode("DK", true);
      this.props.onSetToastMessage({
        message: i18n.t("UI_VALIDATION_ERROR_INVALID_COUNTRY_FROM_CVR_SERVICE"),
        type: "error",
      } as IToastrMessage);
    }

    // Replace company
    this.props.onReplaceCompany({
      ...company,
      address: {
        ...company.address,
        countryName: country.countryName,
        countryCode: country.countryCodeAlpha2,
      },
    });
  }

  public render() {
    const { invoice, selectedPayor, validationErrors, onUpdateCompanyRegistrationIdentifier, onUpdateSelectedPayor, onUpdateCompanyName, onUpdateCoName, onUpdateCombinedAddressLine, onUpdatePostalDistrict, onUpdatePostalCode, onUpdateCountry, onUpdateAreTermsAndConditionsAccepted } = this.props;

    const isValid = this.props.validationErrors.length === 0;

    const isConfirmed = validationErrors.filter((x) => x.property === "areTermsAndConditionsAccepted").length > 0 ? false : true;

    return (
      <div className="confirmation">
        <div className="span6">
          <div className="row-fluid">
            <Card title="TEXT_APPLICANT">
              <CitizenInfoComponent firstName={invoice.application.citizen.firstName} lastName={invoice.application.citizen.lastName} civilRegistrationIdentifier={invoice.application.citizen.civilRegistrationIdentifier} />
            </Card>
          </div>
          <div className="row-fluid">
            <Card title="TEXT_PAYOR">
              <Select
                spanType="span12"
                uiTestAttribute="select-payor"
                onChange={(c: SelectedPayorType) => {
                  onUpdateSelectedPayor(Number(c));
                }}
                label="TEXT_PAYOR"
                value={selectedPayor}
                options={[
                  { key: SelectedPayorType.CitizenPaying, value: i18n.t("TEXT_PAID_BY_APPLICANT") },
                  { key: SelectedPayorType.CompanyPaying, value: i18n.t("TEXT_PAID_BY_OTHER") },
                ]}
              />
              {selectedPayor === SelectedPayorType.CompanyPaying && (
                <div>
                  <div className="row-fluid">
                    <TextField
                      label="TEXT_SEARCH_FOR_CVR"
                      value={invoice.companyBilled.companyRegistrationIdentifier}
                      onChange={(value) => {
                        onUpdateCompanyRegistrationIdentifier(value);
                      }}
                      onEnter={() => {
                        this.getCompanyFromCVR(invoice.companyBilled.companyRegistrationIdentifier, invoice.id);
                      }}
                      uiTestAttribute="field-companyRegistrationIdentifier"
                      submitButton={
                        <button
                          data-ui-test="btn-search"
                          onClick={() => {
                            this.getCompanyFromCVR(invoice.companyBilled.companyRegistrationIdentifier, invoice.id);
                          }}
                          className="btn btn-oim"
                        >
                          {i18n.t("TEXT_SEARCH")} <span className="accessibility"></span>
                          <i className="icon-chevron-right"></i>
                        </button>
                      }
                    />
                  </div>
                  <div className="row-fluid">
                    <TextField className="span12" uiTestAttribute="field-company-name" onChange={(value) => onUpdateCompanyName(value)} label="TEXT_NAME" errorProperty="name" value={invoice.companyBilled.name} />
                  </div>
                  <div className="row-fluid">
                    <TextField className="span12" uiTestAttribute="field-company-address-co-name" onChange={(value) => onUpdateCoName(value)} label="TEXT_CO_NAME" value={invoice.companyBilled.address.coName} />
                  </div>
                  <div className="row-fluid">
                    <TextField className="span12" uiTestAttribute="field-company-address-combined-street-address-line" onChange={(value) => onUpdateCombinedAddressLine(value)} label="TEXT_COMBINED_ADDRESS" value={invoice.companyBilled.address.combinedStreetAddressLine} />
                  </div>
                  <div className="row-fluid">
                    <TextField className="span6" uiTestAttribute="field-company-address-postal-code" onChange={(value) => onUpdatePostalCode(value)} label="TEXT_POSTAL_CODE" value={invoice.companyBilled.address.postalCode} />
                    <TextField className="span6" uiTestAttribute="field-company-address-postal-district" onChange={(value) => onUpdatePostalDistrict(value)} label="TEXT_CITY" value={invoice.companyBilled.address.postalDistrict} />
                  </div>
                  <div className="row-fluid">
                    <Select label={i18n.t("TEXT_COUNTRY_NAME")} onChange={(key: any) => onUpdateCountry(CountryHelper.getCountry(key, true))} value={CountryHelper.getCountryByCountryCode(invoice.companyBilled.address.countryCode, true).id} options={CountryHelper.getCountriesOptionsForAddress()} readonly={false} spanType="span12" uiTestAttribute="field-company-address-country" />
                  </div>
                </div>
              )}
            </Card>
          </div>
        </div>
        <div className="span6">
          <Card title="TEXT_CERTIFICATE">
            <ApplicationInfoComponent certificateNumber={invoice.application.certificateNumber} certificateType={invoice.application.certificateType} certificateProcessType={invoice.application.certificateProcessType} invoiceNumber={invoice.number} invoiceAmount={invoice.amount} invoicePaymentType={invoice.paymentType} invoiceStatus={invoice.status} />
            <hr />
            <table>
              <AcceptTermsAndConditionsComponent areTermsAndConditionsAccepted={invoice.areTermsAndConditionsAccepted} validationErrors={validationErrors} onUpdateAreTermsAndConditionsAccepted={(value) => onUpdateAreTermsAndConditionsAccepted(value)} />
              <tbody>
                <tr>
                  <td colSpan={2}>
                    <form action={process.env.DIBS_URL} method="post">
                      <input name="Id" type="hidden" value={invoice.id} />
                      <input name="AreTermsAndConditionsAccepted" type="hidden" value={invoice.areTermsAndConditionsAccepted.toString()} />
                      {!!invoice.companyBilled && selectedPayor === SelectedPayorType.CompanyPaying && (
                        <span>
                          <input name="CompanyBilled.CompanyRegistrationIdentifier" type="hidden" value={!!invoice.companyBilled.companyRegistrationIdentifier ? invoice.companyBilled.companyRegistrationIdentifier : ""} />
                          <input name="CompanyBilled.Name" type="hidden" value={!!invoice.companyBilled.name ? invoice.companyBilled.name : ""} />
                          <input name="CompanyBilled.Address.CoName" type="hidden" value={!!invoice.companyBilled.address.coName ? invoice.companyBilled.address.coName : ""} />
                          <input name="CompanyBilled.Address.CombinedStreetAddressLine" type="hidden" value={!!invoice.companyBilled.address.combinedStreetAddressLine ? invoice.companyBilled.address.combinedStreetAddressLine : ""} />
                          <input name="CompanyBilled.Address.PostalCode" type="hidden" value={!!invoice.companyBilled.address.postalCode ? invoice.companyBilled.address.postalCode : ""} />
                          <input name="CompanyBilled.Address.PostalDistrict" type="hidden" value={!!invoice.companyBilled.address.postalDistrict ? invoice.companyBilled.address.postalDistrict : ""} />
                          <input name="CompanyBilled.Address.CountryCode" type="hidden" value={!!invoice.companyBilled.address.countryCode ? invoice.companyBilled.address.countryCode : ""} />
                          <input name="CompanyBilled.Address.CountryName" type="hidden" value={!!invoice.companyBilled.address.countryName ? invoice.companyBilled.address.countryName : ""} />
                          <input name="CompanyBilled.Id" type="hidden" value={!!invoice.companyBilled.id ? invoice.companyBilled.id : ""} />
                          <input name="CompanyBilled.Address.Id" type="hidden" value={!!invoice.companyBilled.address.id ? invoice.companyBilled.address.id : ""} />
                        </span>
                      )}
                      <input name="IsInvoicePending" type="hidden" value={invoice.isInvoicePending.toString()} />
                      {selectedPayor === SelectedPayorType.CitizenPaying && (
                        <button disabled={!isValid} type="submit" className="btn btn-oim pull-right" data-ui-test="btn-pay">
                          {i18n.t("TEXT_PAY")} <span className="accessibility"></span> <i className="icon-chevron-right"></i>
                        </button>
                      )}
                      {selectedPayor === SelectedPayorType.CompanyPaying && (
                        <button
                          disabled={!isValid || !isConfirmed}
                          onClick={(e) => {
                            e.preventDefault();
                            this.setState({ showDialog: true });
                          }}
                          className="btn btn-oim pull-right"
                          data-ui-test="btn-pay"
                        >
                          {i18n.t("TEXT_PAY")} <span className="accessibility"></span> <i className="icon-chevron-right"></i>
                        </button>
                      )}
                      <Modal
                        onClose={() => {
                          this.setState({ showDialog: false });
                        }}
                        isShown={this.state.showDialog}
                        title={i18n.t("TEXT_ACCEPT_PAYMENT_FROM_OTHER")}
                        buttons={[
                          <button key="Ok" type="submit" className="btn">
                            {i18n.t("TEXT_OK")}
                          </button>,
                          <button onClick={() => this.setState({ showDialog: false })} key="Close" className="btn">
                            {i18n.t("TEXT_CANCEL")}
                          </button>,
                        ]}
                      >
                        {!!invoice.companyBilled && (
                          <div className="confirmation">
                            <table>
                              <tbody>
                                <tr>
                                  <td>{i18n.t("TEXT_CVR")}: </td>
                                  <td>
                                    <b>{invoice.companyBilled.companyRegistrationIdentifier}</b>
                                  </td>
                                </tr>
                                <tr>
                                  <td>{i18n.t("TEXT_NAME")}: </td>
                                  <td>
                                    <b>{invoice.companyBilled.name}</b>
                                  </td>
                                </tr>
                                <tr>
                                  <td>{i18n.t("TEXT_CO_NAME")}: </td>
                                  <td>
                                    <b>{invoice.companyBilled.address.coName}</b>
                                  </td>
                                </tr>
                                <tr>
                                  <td>{i18n.t("TEXT_COMBINED_ADDRESS")}: </td>
                                  <td>
                                    <b>{invoice.companyBilled.address.combinedStreetAddressLine}</b>
                                  </td>
                                </tr>
                                <tr>
                                  <td>{i18n.t("TEXT_POSTAL_CODE")}: </td>
                                  <td>
                                    <b>{invoice.companyBilled.address.postalCode}</b>
                                  </td>
                                </tr>
                                <tr>
                                  <td>{i18n.t("TEXT_CITY")}: </td>
                                  <td>
                                    <b>{invoice.companyBilled.address.postalDistrict}</b>
                                  </td>
                                </tr>
                                <tr>
                                  <td>{i18n.t("TEXT_COUNTRY_NAME")}: </td>
                                  <td>
                                    <b>{invoice.companyBilled.address.countryName}</b>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        )}
                      </Modal>
                    </form>
                  </td>
                </tr>
              </tbody>
            </table>
            <hr />
            <IssuingAuthorityContactInfoComponent certificateType={invoice.application.certificateType} />
          </Card>
        </div>
      </div>
    );
  }
}
